import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { RadioGroup, RadioGroupItem } from "@bednbody/ui/components/radio-group";
import { Textarea } from "@bednbody/ui/components/textarea";
import { cn } from "@bednbody/ui/lib/utils";
import { useEffect, useRef, useState } from "react";

/**
 * Release a rider from the booking he holds (POST /dispatch/riders/:id/release,
 * 0053). The booking is NOT cancelled — it goes back to waiting for a rider and
 * the dispatcher will not hand it straight back to him (0124/0136).
 *
 * ⚠ PICK, THEN CONFIRM. Each preset used to submit on its own, so a misclick on
 * the wrong line released the rider, sent a `dispatched` booking back to
 * `awaiting_rider` and wrote the wrong reason to `audit_log`, with no undo. A
 * reason is now a selection; Release is a separate press.
 *
 * ⚠ THE ERROR IS SHOWN HERE. The popup stays open on failure, and the message
 * used to be drawn on the page underneath its overlay — the operator saw
 * nothing happen and pressed again.
 *
 * ⚠ WAS "Cancel dispatch". The action frees the rider; the client's booking
 * stays alive. "Cancel" read as cancelling the booking.
 *
 * ⚠ WAS A HAND-ROLLED `fixed inset-0` OVERLAY (`frontend-standards.md` §6). The
 * package `Dialog` brings the enter/exit animation, Escape, and a focus trap.
 */
const PRESET_REASONS = [
  "Stuck — booking behind it was already cancelled",
  "Wrong rider was assigned",
  "Rider requested release",
  "Duplicate/erroneous assignment",
];

const OTHER = "other";

export function ReleaseDispatchModal({
  riderName,
  isPending,
  errorMessage,
  onConfirm,
  onClose,
}: {
  /** Null closes it. The parent keeps this mounted so the exit can animate. */
  riderName: string | null;
  isPending: boolean;
  errorMessage: string | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState("");
  const [customReason, setCustomReason] = useState("");

  // The name outlives the close, so the dialog does not blank mid-fade.
  const shownName = useRef<string | null>(riderName);
  useEffect(() => {
    if (riderName) shownName.current = riderName;
  }, [riderName]);

  // ⚠ RESET WHEN IT CLOSES. Without this, releasing a second rider reopens on
  // the reason picked for the first one — and this submits to an audited,
  // irreversible endpoint.
  useEffect(() => {
    if (riderName === null) {
      setChoice("");
      setCustomReason("");
    }
  }, [riderName]);

  const reason = choice === OTHER ? customReason.trim() : choice;
  const canConfirm = !isPending && (choice === OTHER ? reason.length >= 3 : reason !== "");

  return (
    <Dialog
      open={riderName !== null}
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Release rider</DialogTitle>
          <DialogDescription>
            {shownName.current ?? "This rider"} is taken off the booking and freed. The booking is
            not cancelled — it goes back to waiting for a rider.
          </DialogDescription>
        </DialogHeader>

        <fieldset disabled={isPending}>
          <legend className="mb-2 text-sm font-medium">Why?</legend>
          <RadioGroup value={choice} onValueChange={(v) => setChoice(v as string)}>
            <div className="space-y-1.5">
              {[...PRESET_REASONS, OTHER].map((value, i) => (
                <label
                  key={value}
                  htmlFor={`release-reason-${i}`}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors",
                    choice === value ? "border-primary bg-primary/5" : "border-border",
                  )}
                >
                  <RadioGroupItem id={`release-reason-${i}`} value={value} />
                  <span className="flex-1">{value === OTHER ? "Other…" : value}</span>
                </label>
              ))}
            </div>
          </RadioGroup>
        </fieldset>

        {choice === OTHER ? (
          <div className="space-y-1">
            <label htmlFor="release-reason-text" className="sr-only">
              What happened?
            </label>
            <Textarea
              id="release-reason-text"
              autoFocus
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="What happened? (min 3 characters)"
              rows={3}
              maxLength={500}
            />
          </div>
        ) : null}

        {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Keep rider
          </Button>
          <Button variant="destructive" disabled={!canConfirm} onClick={() => onConfirm(reason)}>
            {isPending ? "Releasing…" : "Release rider"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
